import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { useCookies } from 'react-cookie';
import clsx from 'clsx';

import createApolloClient from '../api/index';

import getAllPages from '../api/queries/pages/getAllPages';
import getAllArticles from '../api/queries/articles/getAllArticles';
import getAllPosts from '../api/queries/posts/getAllPosts';
import getAllTemplates from '../api/queries/template/getAllTemplates';
import getMenu from '../api/queries/menus/getMenu';
import getAllTopPages from '../api/queries/topPages/getAllTopPages';
import getPageSchema from '../utils/getPageSchema';
import getAllReviews from '../api/queries/reviews/getAllReviews';

import PageLayout from '../components/PageLayout';
import Sidebar from '../components/Sidebar';
import Breadcrumb from '../components/Breadcrumb';

import Article from '../postTypes/Article';
import Page from '../postTypes/Page';
import Template from '../postTypes/Template';

import getPostTypeBySlug from '../api/queries/getPostTypeBySlug';
import queryAllItems from '../api/utils/queryAllItems';

import getArticleSlug from '../utils/getArticleSlug';
import getPageSlug from '../utils/getPageSlug';
import getTemplateSlug from '../utils/getTemplateSlug';
import getStaticPropsByPostType from '../utils/getStaticPropsByPostType';
import postTypes from '../utils/postTypes';
import templates from '../utils/templates';
import getSEOFromPostype from '../utils/getSEOFromPostype';
import getPageIndexStatus from '../utils/getPageIndexStatus';
import getPostSlug from '../utils/getPostSlug';
import Blog from '../postTypes/Blog';

const client = createApolloClient();

const DynamicPostypes = ({
  postTypeData,
  postType,
  desktopHeaderData,
  aboutMenuData,
  toolsMenuData,
  customReviewsData,
  topPages,
}) => {
  const [headings, setHeadings] = useState([]);
  const [pageSchema, setPageSchema] = useState([]);
  const [cookie, setCookie] = useCookies(['internalUser']);
  const router = useRouter();
  const isUserLoggedIn = router.query.slug[0] === 'contact-us' && router.query.loggedIn === 'true';
  const isUserLogged = router.query.slug[0] === 'contact-us' && router.query.logged === 'true';
  const isInternalUser = router.query.internaluser === 'true';
  const { data } = postTypeData;
  const { template } = data?.pageBy || {};
  const isPage = postType === postTypes.PAGE;
  const isPost = postType === postTypes.POST;
  const isTemplate = postType === postTypes.TEMPLATE;
  const yoastSeo = getSEOFromPostype(postType, data);
  const indexStatus = getPageIndexStatus(postType, data);
  const isNoSidebarTemplate = template?.templateName === templates.NO_SIDEBAR;
  const isHomeage = template?.templateName === templates.HOMEPAGE;
  const isSidebarHidden = isTemplate || isPost || (isPage && isNoSidebarTemplate);
  const classes = clsx({
    'page-layout--has-sidebar': !isSidebarHidden && !isHomeage,
  });
  const pageTypeClasses = clsx({
    'ms-page': !isHomeage && !isPost,
  });
  let DynamicPostType = null;

  const { pageBy = '' } = data;
  const { title, slug } = pageBy;
  const pageTitles = title ? title.split(' ').join('-').toLowerCase() : '';
  const pageTitle = slug;

  useEffect(() => {
    const headingEls = Array.from(document.querySelectorAll('.ms-page h2, .page h2'));
    const myElement = document.getElementsByClassName('section__text');
    const contentArray = [];
    const headingArray = [];
    for (let i = 0; i < myElement.length; i++) {
      if (myElement[i].firstChild) {
        if (
          myElement[i].firstChild.localName === 'h2' &&
          myElement[i].firstChild.innerText.includes('?')
        ) {
          headingArray.push(myElement[i].children[0]);
          contentArray.push(myElement[i].textContent);
        }
      }
    }
    const heading = headingArray.map((o) => o.innerText);
    const content = contentArray.map((o) => o.substring(o.indexOf('?') + 1).replace(/\n/g, ''));

    const pageSchema = Object.keys(heading).map((key) => ({
      title: heading[key],
      content: content[key],
    }));

    setHeadings(headingEls);
    setPageSchema(pageSchema);
  }, []);

  const excludedPages = [];

  const pageSchemaSet =
    pageSchema.length > 1 && !excludedPages.includes(pageTitle)
      ? JSON.stringify(getPageSchema(pageSchema))
      : '';

  if (isInternalUser || isUserLoggedIn || isUserLogged) {
    setCookie('internalUser', 'true', {
      path: '/',
    });
  }

  switch (postType) {
    case postTypes.ARTICLE:
      DynamicPostType = Article;
      break;

    case postTypes.POST:
      DynamicPostType = Blog;
      break;

    case postTypes.PAGE:
      DynamicPostType = Page;
      break;

    case postTypes.TEMPLATE:
      DynamicPostType = Template;
      break;

    default:
      break;
  }
  return (
    <PageLayout
      menuData={desktopHeaderData}
      aboutMenuData={aboutMenuData}
      toolsMenuData={toolsMenuData}
      classNames={`${classes} ${pageTitle} ${pageTypeClasses}`}
      yoastSeo={yoastSeo}
      pageSchemaSet={pageSchemaSet}
      indexStatus={indexStatus}
    >
      {!isHomeage && !isPost && <Breadcrumb data={data} />}
      <div className="ms-page__main">
        <DynamicPostType data={data} headings={headings} customReviewsData={customReviewsData} />
        {!isSidebarHidden && !isHomeage && (
          <Sidebar data={data} headings={headings} isSidebarHidden={isSidebarHidden} />
        )}
      </div>
      {/* <Html unsafeHtml={html} /> */}
    </PageLayout>
  );
};

export default DynamicPostypes;

export const getStaticPaths = async () => {
  const pagesData = await queryAllItems(client.query, getAllPages);
  const articlesData = await queryAllItems(client.query, getAllArticles);
  const postsData = await queryAllItems(client.query, getAllPosts);
  const templatesData = await queryAllItems(client.query, getAllTemplates);
  const result = [
    ...postsData.map(getPostSlug),
    ...pagesData.map(getPageSlug),
    ...articlesData.map(getArticleSlug),
    ...templatesData.map(getTemplateSlug),
  ];

  const paths = result.map((slug) => ({
    params: { slug: slug },
  }));

  return {
    paths,
    fallback: false,
  };
};

export async function getStaticProps({ params }) {
  const { slug } = params;
  const lastSlug = slug[slug.length - 1];
  const customPostTypeQuery = getPostTypeBySlug(lastSlug);
  const topPages = (await client.query(getAllTopPages()))?.data?.acfOptionsTopPages?.topPages?.topPages;
  const {
    data: { getPostTypeBySlug: postType },
  } = await client.query(customPostTypeQuery);
  
  const getStaticPropsFromCustomPostType = getStaticPropsByPostType(postType);
  const postTypeData = await client.query(getStaticPropsFromCustomPostType(`/${lastSlug}`));
  if (!postTypeData.data.articleBy && !postTypeData.data.postBy && !postTypeData.data.templateBy && !postTypeData.data.pageBy) {
    console.error("\nerror: Post", slug.join('/'), "has incorrect state or no data. The post will be skipped.\n");
    return {
      notFound: true,
    };
  }

  const desktopHeaderData = await client.query(getMenu('dGVybToyNQ=='));
  const aboutMenuData = await client.query(getMenu('dGVybTo1NQ=='));
  const toolsMenuData = await client.query(getMenu('dGVybTo1Ng=='));
  const customReviewsData = await client.query(getAllReviews());

  return {
    props: {
      postTypeData,
      postType,
      desktopHeaderData,
      aboutMenuData,
      toolsMenuData,
      customReviewsData,
      topPages,
    },
    revalidate: 10,
  };
}
